import { useCallback, useEffect, useRef, useState } from 'react'
import type { AnchorHTMLAttributes, JSX, ReactNode } from 'react'
import {
  ApiError,
  fetchAccountOnboarding,
  fetchAccounts,
  fetchDeployment,
  fetchDeployments,
  fetchFleet,
  fetchMe,
  fetchRun,
  fetchTenants,
  NotAuthenticated,
} from './api.js'
import { isRole, roleHasCapability } from '@qim/shared'
import { beginSignIn, completeSignIn, currentToken } from './auth/session.js'
import { pathFor, viewFor } from './routing.js'
import { Access } from './views/access.jsx'
import { Account } from './views/account.jsx'
import { Audit } from './views/audit.jsx'
import { Customer } from './views/customer.jsx'
import { Customers } from './views/customers.jsx'
import { Fleet } from './views/fleet.jsx'
import { Palette } from './views/palette.jsx'
import { Run } from './views/run.jsx'
import { Deployment } from './views/deployment.jsx'
import type { AppConfig } from './config.js'
import type {
  AccountOnboarding,
  AccountSummary,
  DeploymentDetail,
  DeploymentSummary,
  Me,
  RunSummary,
  TenantSummary,
} from './api.js'
import type { Fleet as FleetData } from '@qim/shared'
import type { View } from './routing.js'

type State =
  | { readonly status: 'starting' }
  | { readonly status: 'signed-out' }
  | { readonly status: 'signing-in' }
  | { readonly status: 'signed-in'; readonly me: Me }
  | { readonly status: 'error'; readonly message: string }

/**
 * What the current screen needs, once it has it.
 *
 * One shape per screen rather than a bag of optional fields, so rendering a
 * screen without its data is not an expressible state — the same reason the API
 * discriminates its unions.
 */
type Screen =
  | { readonly of: 'loading' }
  | { readonly of: 'fleet'; readonly fleet: FleetData }
  | { readonly of: 'customers'; readonly tenants: readonly TenantSummary[] }
  | {
      readonly of: 'customer'
      readonly tenantId: string
      readonly accounts: readonly AccountSummary[]
      readonly deployments: readonly DeploymentSummary[]
    }
  | {
      readonly of: 'deployment'
      readonly tenantId: string
      readonly deployment: DeploymentDetail
    }
  | {
      readonly of: 'account'
      readonly tenantId: string
      readonly onboarding: AccountOnboarding
    }
  | {
      readonly of: 'run'
      readonly tenantId: string
      readonly runId: string
      readonly run: RunSummary
    }
  /** The audit screen loads its own data, so there is nothing to carry here. */
  | { readonly of: 'audit' }
  /** The access screen loads its own data, so there is nothing to carry here. */
  | { readonly of: 'access' }
  | { readonly of: 'missing' }
  /**
   * The API answered, and it was not a 404.
   *
   * Separate from `missing` because the two send someone to different places:
   * "not found" says check the link, and a 500 says the link was right and
   * something broke behind it. They were the same state until a brand-new
   * account 500'd the onboarding screen and the console said the link might be
   * wrong — which is where Amit went looking (IM-121).
   */
  | { readonly of: 'failed'; readonly status: number }

const describeScope = (scope: Me['scope']): string =>
  scope.kind === 'all'
    ? 'All customers'
    : scope.tenantIds.length === 0
      ? 'No customers assigned'
      : scope.tenantIds.join(', ')

/**
 * A link that navigates in place, without losing cmd-click.
 *
 * The same six lines were written out at each of the console's link sites, and
 * two of them had forgotten the modifier check — so cmd-clicking a breadcrumb
 * swallowed the event and opened nothing. Middle-click and cmd-click open a tab
 * because the href is real; that is the whole reason these are anchors.
 */
const Link = ({
  to,
  onOpen,
  children,
  ...rest
}: {
  readonly to: string
  readonly onOpen: (path: string) => void
  readonly children: ReactNode
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>): JSX.Element => (
  <a
    href={to}
    onClick={(event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
      event.preventDefault()
      onOpen(to)
    }}
    {...rest}
  >
    {children}
  </a>
)

export const App = ({ config }: { config: AppConfig }): JSX.Element => {
  const [state, setState] = useState<State>({ status: 'starting' })
  const [view, setView] = useState<View>(() => viewFor(window.location.pathname))
  const [screen, setScreen] = useState<Screen>({ of: 'loading' })
  /**
   * Bumped by a write, so the current screen reloads.
   *
   * A screen that has just created a customer or registered an account must not
   * still be showing the list from before it — and re-fetching is honest in a
   * way that splicing the new record into local state is not, because the API
   * decides what the caller may see.
   */
  const [reloadToken, setReloadToken] = useState(0)
  /*
   * Whether hidden records are shown (IM-112).
   *
   * Held here rather than in a view because it changes what is *fetched* — the
   * API filters, not the browser, so a toggle that only hid rows locally would
   * be a second opinion about what the caller may see. It is in the loader's
   * dependency list, so flipping it re-reads.
   *
   * Not in the URL: it is a momentary "let me look" rather than a place, and a
   * deep link into a ticket should show what everyone else sees.
   */
  const [showHidden, setShowHidden] = useState(false)
  const reload = useCallback(() => setReloadToken((n) => n + 1), [])
  /**
   * The view the current `screen` was loaded for.
   *
   * Only a *navigation* blanks the screen to `loading`. A re-read after a write
   * must leave it mounted, and that is a correctness requirement rather than a
   * matter of flicker: `RegisterAccount` holds the freshly minted external ID in
   * its own state, and that value is shown exactly once with no read path
   * afterwards. Unmounting the screen to reload the list behind it therefore
   * destroyed the only copy of a secret the customer needs — IM-88, and the
   * reason `onRegistered` now fires on dismissal rather than on success.
   */
  const loadedFor = useRef<View | undefined>(undefined)

  /**
   * What the jump-to palette searches (IM-106).
   *
   * Loaded once when signing in rather than per screen, because the palette
   * opens from anywhere and a keystroke must not wait on a request. Both reads
   * are already scoped server-side, so holding them here adds no decision the
   * console has to make.
   *
   * A failure is silent on purpose: the palette is a shortcut, and every
   * destination in it is reachable by navigating. Interrupting someone's screen
   * to say a convenience did not load would be worse than the convenience being
   * briefly empty.
   */
  const [jumpFleet, setJumpFleet] = useState<FleetData | undefined>(undefined)
  const [jumpTenants, setJumpTenants] = useState<readonly TenantSummary[]>([])
  const [paletteOpen, setPaletteOpen] = useState(false)
  /** Where focus was when the palette opened, so Esc gives it back. */
  const cameFrom = useRef<HTMLElement | null>(null)

  /**
   * Navigates without a reload, and keeps the back button working.
   *
   * pushState plus a popstate listener rather than a router library: three
   * screens do not justify the dependency, and the whole of the behaviour is
   * these dozen lines.
   */
  const open = useCallback((path: string) => {
    window.history.pushState({}, '', path)
    setView(viewFor(path))
  }, [])

  useEffect(() => {
    if (state.status !== 'signed-in') return
    let live = true
    void Promise.allSettled([fetchFleet(config.apiBaseUrl), fetchTenants(config.apiBaseUrl)]).then(
      ([fleet, tenants]) => {
        if (!live) return
        if (fleet.status === 'fulfilled') setJumpFleet(fleet.value)
        if (tenants.status === 'fulfilled') setJumpTenants(tenants.value.tenants)
      },
    )
    return () => {
      live = false
    }
  }, [state.status, config.apiBaseUrl, reloadToken])

  /*
   * The one global shortcut. Meta on a Mac, Control elsewhere — reading both
   * rather than sniffing the platform, since either is unambiguous here.
   *
   * Ignored while focus is in a text field, so typing a deployment id into the
   * run form cannot be swallowed by a navigation shortcut.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return
      const target = event.target
      const inField =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable === true)
      if (inField) return
      event.preventDefault()
      cameFrom.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null
      setPaletteOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [])

  /*
   * Focus cannot leave an open modal, so it is returned after the dialog has
   * gone — from an effect, once React has committed the unmount. Calling
   * `focus()` in the close handler silently does nothing (IM-79).
   */
  useEffect(() => {
    if (paletteOpen) return
    const origin = cameFrom.current
    cameFrom.current = null
    origin?.focus()
  }, [paletteOpen])

  useEffect(() => {
    const onPop = (): void => {
      setView(viewFor(window.location.pathname))
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setState({ status: 'signed-in', me: await fetchMe(config.apiBaseUrl) })
    } catch (error: unknown) {
      if (error instanceof NotAuthenticated) {
        setState({ status: 'signed-out' })
        return
      }
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [config.apiBaseUrl])

  useEffect(() => {
    const run = async (): Promise<void> => {
      // Returning from Identity Center: finish the exchange, then clear the code
      // from the URL so a reload cannot replay it and the address bar carries no
      // credential material.
      if (window.location.search.includes('code=')) {
        setState({ status: 'signing-in' })
        try {
          await completeSignIn(config.auth, window.sessionStorage, window.location.search)
          window.history.replaceState({}, '', window.location.pathname)
          await load()
        } catch (error: unknown) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Sign-in failed',
          })
        }
        return
      }

      if (currentToken() === undefined) {
        setState({ status: 'signed-out' })
        return
      }
      await load()
    }
    void run()
  }, [config.auth, load])

  /**
   * Loads whatever the current screen needs.
   *
   * Re-runs on navigation. A failure shows the shell with an error rather than
   * a blank page: the API returns the same 404 for a deployment out of scope as
   * for one that never existed, so "not found" is the only honest thing to say.
   */
  useEffect(() => {
    if (state.status !== 'signed-in') {
      // So that signing back in blanks rather than showing the last session's
      // screen while the first fetch is in flight.
      loadedFor.current = undefined
      return
    }
    let live = true
    const load = async (): Promise<void> => {
      if (loadedFor.current !== view) setScreen({ of: 'loading' })
      loadedFor.current = view
      try {
        if (view.screen === 'fleet') {
          // One request. The scoping already happened server-side, so there is
          // nothing here to filter — see `fleet.tsx`.
          if (live) setScreen({ of: 'fleet', fleet: await fetchFleet(config.apiBaseUrl) })
          return
        }
        if (view.screen === 'customers') {
          const { tenants } = await fetchTenants(config.apiBaseUrl, showHidden)
          if (live) setScreen({ of: 'customers', tenants })
          return
        }
        if (view.screen === 'customer') {
          // Together: neither is useful without the other, and two spinners on
          // one screen is worse than one slightly longer wait.
          const [accounts, deployments] = await Promise.all([
            fetchAccounts(config.apiBaseUrl, view.tenantId),
            fetchDeployments(config.apiBaseUrl, view.tenantId),
          ])
          if (live) {
            setScreen({
              of: 'customer',
              tenantId: view.tenantId,
              accounts: accounts.accounts,
              deployments: deployments.deployments,
            })
          }
          return
        }
        if (view.screen === 'run') {
          const run = await fetchRun(config.apiBaseUrl, view.tenantId, view.runId)
          if (live) {
            setScreen({ of: 'run', tenantId: view.tenantId, runId: view.runId, run })
          }
          return
        }
        if (view.screen === 'audit') {
          // Loads and reloads itself as filters change, so there is nothing to
          // fetch before rendering it.
          if (live) setScreen({ of: 'audit' })
          return
        }
        if (view.screen === 'access') {
          if (live) setScreen({ of: 'access' })
          return
        }
        if (view.screen === 'account') {
          const onboarding = await fetchAccountOnboarding(
            config.apiBaseUrl,
            view.tenantId,
            view.accountKey,
          )
          if (live) setScreen({ of: 'account', tenantId: view.tenantId, onboarding })
          return
        }
        const deployment = await fetchDeployment(
          config.apiBaseUrl,
          view.tenantId,
          view.deploymentId,
        )
        if (live) setScreen({ of: 'deployment', tenantId: view.tenantId, deployment })
      } catch (error: unknown) {
        if (!live) return
        if (error instanceof NotAuthenticated) {
          setState({ status: 'signed-out' })
          return
        }
        /*
         * A 404 is "not found"; anything else is not. The API deliberately
         * answers 404 for a tenant out of scope exactly as for one that never
         * existed, so that case must stay indistinguishable — but a 500 is
         * neither, and calling it "not found" sends someone to check a URL that
         * was fine.
         */
        setScreen(
          error instanceof ApiError && error.status !== 404
            ? { of: 'failed', status: error.status }
            : { of: 'missing' },
        )
      }
    }
    void load()
    return () => {
      live = false
    }
  }, [state.status, view, config.apiBaseUrl, reloadToken, showHidden])

  const signIn = useCallback(() => {
    void beginSignIn(config.auth, window.sessionStorage).then((url) => {
      window.location.assign(url)
    })
  }, [config.auth])

  /**
   * The trail to where you are — and only that.
   *
   * Until IM-73 this element also carried the links to Audit and Access, which
   * produced "Customers / Audit · Audit · Access" on the audit screen: the path
   * you took and the places you can go, interleaved. They are separate landmarks
   * now, and this one names no destination that is not above the current screen.
   */
  const crumbs =
    // Nothing above the fleet, so there is no trail to draw: a breadcrumb
    // reading "Fleet" beside a nav item reading "Fleet" and a heading reading
    // "Fleet" is three of the same word.
    view.screen === 'fleet' ? null : (
      <nav aria-label="Breadcrumb" className="crumbs">
        <Link to={pathFor({ screen: 'fleet' })} onOpen={open}>
          Fleet
        </Link>
        {view.screen === 'customers' ? (
          <>
            <span className="sep">/</span>
            <span className="here">Customers</span>
          </>
        ) : (
          <>
            <span className="sep">/</span>
            <Link to={pathFor({ screen: 'customers' })} onOpen={open}>
              Customers
            </Link>
          </>
        )}
        {view.screen === 'audit' ||
        view.screen === 'access' ||
        view.screen === 'customers' ? null : (
          <>
            <span className="sep">/</span>
            {view.screen === 'customer' ? (
              <span className="here">{view.tenantId}</span>
            ) : (
              <Link to={pathFor({ screen: 'customer', tenantId: view.tenantId })} onOpen={open}>
                {view.tenantId}
              </Link>
            )}
          </>
        )}
        {view.screen === 'deployment' ? (
          <>
            <span className="sep">/</span>
            <span className="here">{view.deploymentId}</span>
          </>
        ) : null}
        {view.screen === 'run' ? (
          <>
            <span className="sep">/</span>
            <span className="here">{view.runId}</span>
          </>
        ) : null}
        {view.screen === 'account' ? (
          <>
            <span className="sep">/</span>
            <span className="here">{view.accountKey}</span>
          </>
        ) : null}
        {view.screen === 'audit' ? (
          <>
            <span className="sep">/</span>
            <span className="here">Audit</span>
          </>
        ) : null}
        {view.screen === 'access' ? (
          <>
            <span className="sep">/</span>
            <span className="here">Access</span>
          </>
        ) : null}
      </nav>
    )

  return (
    <>
      <header className="app-bar">
        <h1 className="brand">
          <span className="brand-mark" aria-hidden="true" />
          Qrvey Instance Manager
        </h1>
        {/* Which environment this is, said where nobody has to look for it: the
          same console serves dev and production, and that word is the whole
          difference between a harmless apply and a serious one. */}
        <p className="env-chip">{config.environment}</p>

        {state.status === 'signed-in' ? (
          <>
            <nav aria-label="Main" className="main-nav">
              <Link
                to={pathFor({ screen: 'fleet' })}
                onOpen={open}
                {...(view.screen === 'fleet' ? { 'aria-current': 'page' as const } : {})}
              >
                Fleet
              </Link>
              <Link
                to={pathFor({ screen: 'customers' })}
                onOpen={open}
                {...(view.screen === 'customers' ? { 'aria-current': 'page' as const } : {})}
              >
                Customers
              </Link>
              {/* Offered only to a role that holds audit:read. Not a control —
                the API refuses either way — but a link that always 403s is a
                worse way to say no than no link. */}
              {isRole(state.me.role) && roleHasCapability(state.me.role, 'audit:read') ? (
                <Link
                  to={pathFor({ screen: 'audit' })}
                  onOpen={open}
                  {...(view.screen === 'audit' ? { 'aria-current': 'page' as const } : {})}
                >
                  Audit
                </Link>
              ) : null}
              {/* Same reasoning, for access:manage. */}
              {isRole(state.me.role) && roleHasCapability(state.me.role, 'access:manage') ? (
                <Link
                  to={pathFor({ screen: 'access' })}
                  onOpen={open}
                  {...(view.screen === 'access' ? { 'aria-current': 'page' as const } : {})}
                >
                  Access
                </Link>
              ) : null}
            </nav>
            {/* Moved out of the footer: who you are signed in as and what you
              can see is context for reading the screen, not a postscript to it.
              This is how someone notices they are the wrong person for the job
              in front of them. */}
            {/* Discoverable, not just documented: a shortcut nobody knows
              about is a shortcut nobody uses. */}
            <button
              type="button"
              className="jump"
              onClick={() => {
                cameFrom.current =
                  document.activeElement instanceof HTMLElement ? document.activeElement : null
                setPaletteOpen(true)
              }}
            >
              Jump to…<kbd>⌘K</kbd>
            </button>
            <p className="who">
              <strong>{state.me.email}</strong>
              {state.me.role} · {describeScope(state.me.scope)}
            </p>
          </>
        ) : null}
      </header>

      <main>
        {state.status === 'starting' || state.status === 'signing-in' ? (
          <p role="status">Signing in…</p>
        ) : null}

        {state.status === 'signed-out' ? (
          <section className="panel">
            <h2>Sign in</h2>
            <p>You need to sign in with your Qrvey account.</p>
            <button type="button" onClick={signIn}>
              Sign in
            </button>
          </section>
        ) : null}

        {state.status === 'error' ? (
          <section className="panel">
            <h2>Sign-in failed</h2>
            <p role="alert">{state.message}</p>
            <button type="button" onClick={signIn}>
              Try again
            </button>
          </section>
        ) : null}

        {state.status === 'signed-in' ? (
          <>
            {paletteOpen ? (
              <Palette
                fleet={jumpFleet}
                tenants={jumpTenants}
                onOpen={open}
                onClose={() => setPaletteOpen(false)}
              />
            ) : null}

            {crumbs}

            {screen.of === 'loading' ? <p role="status">Loading…</p> : null}

            {screen.of === 'failed' ? (
              <p role="alert">
                The link is fine — something went wrong answering it ({String(screen.status)}). Try
                again; if it keeps happening the API log has the reason.
              </p>
            ) : null}

            {screen.of === 'missing' ? (
              // Deliberately not "you do not have access": the API cannot tell
              // us which it is, and guessing would confirm that another
              // customer's deployment exists.
              <p role="alert">Not found. It may have been removed, or the link may be wrong.</p>
            ) : null}

            {screen.of === 'fleet' ? (
              <Fleet
                fleet={screen.fleet}
                onOpen={open}
                baseUrl={config.apiBaseUrl}
                role={state.me.role}
              />
            ) : null}

            {screen.of === 'customers' ? (
              <Customers
                baseUrl={config.apiBaseUrl}
                me={state.me}
                tenants={screen.tenants}
                onOpen={open}
                onChanged={reload}
                showHidden={showHidden}
                onShowHidden={setShowHidden}
              />
            ) : null}

            {screen.of === 'customer' ? (
              <Customer
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                kind={jumpTenants.find((t) => t.tenantId === screen.tenantId)?.kind}
                role={state.me.role}
                accounts={screen.accounts}
                deployments={screen.deployments}
                onOpen={open}
                onChanged={reload}
              />
            ) : null}

            {screen.of === 'run' ? (
              <Run
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                runId={screen.runId}
                initial={screen.run}
              />
            ) : null}

            {screen.of === 'audit' ? <Audit baseUrl={config.apiBaseUrl} /> : null}

            {screen.of === 'access' ? <Access baseUrl={config.apiBaseUrl} /> : null}

            {screen.of === 'account' ? (
              <Account
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                onboarding={screen.onboarding}
                role={state.me.role}
                onChanged={reload}
              />
            ) : null}

            {screen.of === 'deployment' ? (
              <Deployment
                baseUrl={config.apiBaseUrl}
                tenantId={screen.tenantId}
                role={state.me.role}
                deployment={screen.deployment}
                onOpen={open}
                onChanged={reload}
              />
            ) : null}
          </>
        ) : null}
      </main>
    </>
  )
}
